import { skipToken, useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import * as React from 'react';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { FREE_FONTS, THEME_PRESETS } from '@gitbook/app-database';
import type { NavigationScreenState } from '@gitbook/app-navigation';
import {
    DEFAULT_HINT_DANGER_COLOR,
    DEFAULT_HINT_INFO_COLOR,
    DEFAULT_HINT_SUCCESS_COLOR,
    DEFAULT_HINT_WARNING_COLOR,
} from '@gitbook/colors';
import { Icon as FAIcon, type IconName as FAIconName } from '@gitbook/icons';
import type { PickerItemData } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Actions,
    Button,
    type ButtonProps,
    Heading,
    PanelSetContext,
    Tab,
    Tabs,
    toast,
} from '@gitbook/spine-react';
import { useEventCallback, useLoadingState, useStateWithDeps } from '@gitbook/util-react-hooks';
import { assertNever } from '@gitbook/util-ts';

import { BlockNavigationModal, useNavigation, useSetAnalyticsScreen } from '../../navigation';
import {
    apiClient,
    apiQuery,
    getSiteSectionsFromSiteSectionGroup,
    getSiteSpacesFromStructure,
    isSelfServeMigrateAction,
    isSiteUpgradeAction,
    useSiteContext,
    useUpgradeSite,
} from '../../state';
import { ContentReferenceContextProvider } from '../ContentReferences';
import { CustomizationSiteSpacePicker } from './CustomizationSiteSpacePicker';
import { SiteCustomizationPreview } from './SiteCustomizationPreview';
import { TabConfigure } from './TabConfigure';
import { TabGeneral } from './TabGeneral';
import { TabLayout } from './TabLayout';
import type { SiteCustomizationBodyProps, SitesCustomizationPanelProps } from './types';
import { computeSettingsOverrides, diffCustomizationSettings, SITE_THEMES } from './utils';

/**
 * Whole modal of the customization flow.
 */
export function SiteCustomizationBody(
    props: SiteCustomizationBodyProps
): React.ReactElement | null {
    const { site, organizationId, analyticsContext } = useSiteContext();
    const { siteSpaceId, setSelectedTab, selectedTab } = props;

    const [loading, wrapLoading] = useLoadingState();
    const { navigate } = useNavigation();

    // Compute a key to refresh all elements when data changes.
    const settingsKey = `${site.id}-${siteSpaceId ?? 'site'}`;

    const [
        { data: organization },
        { data: siteStructure },
        { data: siteSettings },
        { data: spaceSettings },
    ] = useSuspenseQueries({
        queries: [
            apiQuery('getOrganizationById', { organizationId }),
            apiQuery('getSiteStructure', { organizationId, siteId: site.id }),
            apiQuery('getSiteRawCustomizationById', { organizationId, siteId: site.id }),
            apiQuery(
                'getSiteSpaceRawCustomizationById',
                siteSpaceId
                    ? {
                          organizationId,
                          siteId: site.id,
                          siteSpaceId,
                      }
                    : skipToken
            ),
        ],
    });

    const siteSpaces = React.useMemo(
        () => getSiteSpacesFromStructure(siteStructure),
        [siteStructure]
    );

    /**
     * If the site has only one site-space, we fetch the customization settings for that site-space
     * to check if it has any overrides. If it does, we always show the picker.
     */
    const singleSiteSpace = (siteSpaces.length === 1 ? siteSpaces[0] : null) ?? null;
    const { data: onlySiteSpaceVariantSettings } = useSuspenseQuery(
        apiQuery(
            'getSiteSpaceRawCustomizationById',
            singleSiteSpace && !siteSpaceId
                ? {
                      organizationId,
                      siteId: site.id,
                      siteSpaceId: siteSpaces[0].id,
                  }
                : skipToken
        )
    );
    // The picker should be shown if there are multiple site spaces, or if there is only one site space and it has overrides.
    const shouldShowPicker =
        siteSpaces.length > 1 || siteSpaceId || onlySiteSpaceVariantSettings?.hasOverrides;

    const currentSettings = spaceSettings ?? siteSettings;

    useSetAnalyticsScreen(
        {
            ...analyticsContext,
            screen: 'site_customize',
            screenTab: selectedTab,
        },
        [analyticsContext, selectedTab]
    );

    const siteSpacesPickerItems = React.useMemo<PickerItemData[]>(() => {
        const items: PickerItemData[] = [
            {
                label: 'Site-wide settings',
                value: 'site',
                icon: Icon.Globe,
            },
        ];

        const fillSection = (siteSection: api.SiteSection, prefix?: string) => {
            const singleSiteSpace =
                siteSection.siteSpaces.length === 1 ? siteSection.siteSpaces[0] : null;
            if (singleSiteSpace) {
                items.push({
                    label: [prefix, siteSection.title].filter((x) => x != null).join(' > '),
                    value: singleSiteSpace.id,
                    icon: siteSection.icon ? (
                        <FAIcon icon={siteSection.icon as FAIconName} />
                    ) : (
                        Icon.Documentation
                    ),
                });
            } else {
                siteSection.siteSpaces.forEach((siteSpace) => {
                    items.push({
                        label: [prefix, siteSection.title, siteSpace.title]
                            .filter((x) => x != null)
                            .join(' > '),
                        value: siteSpace.id,
                        icon: Icon.Documentation,
                    });
                });
            }
        };

        switch (siteStructure.type) {
            case 'siteSpaces': {
                items.push(
                    ...siteStructure.structure.map((siteSpace) => ({
                        label: siteSpace.title,
                        value: siteSpace.id,
                        icon: Icon.Documentation,
                    }))
                );
                break;
            }
            case 'sections': {
                siteStructure.structure.forEach((sectionOrGroup) => {
                    switch (sectionOrGroup.object) {
                        case 'site-section': {
                            fillSection(sectionOrGroup);
                            break;
                        }
                        case 'site-section-group': {
                            return getSiteSectionsFromSiteSectionGroup(sectionOrGroup).forEach(
                                (section) => fillSection(section)
                            );
                        }
                        default:
                            assertNever(sectionOrGroup);
                    }
                });
                break;
            }
            default:
                assertNever(siteStructure);
        }

        return items;
    }, [siteStructure]);

    // State for customization settings with dependency on current settings
    const [newSettings, setNewSettings] = useStateWithDeps(currentSettings, [currentSettings]);

    const upgrade = useUpgradeSite(site, api.SiteFeatureKey.SitesAdvancedCustomization);

    const isSiteUpgrade = isSiteUpgradeAction(upgrade);
    const isSelfServeMigrate = isSelfServeMigrateAction(upgrade);
    const usesUnpaidCustomizations =
        checkHasAnyEnabledPaidCustomizationSetting(newSettings) &&
        (isSiteUpgrade || isSelfServeMigrate);

    // Calculate the diff of the settings
    const diff = React.useMemo(() => {
        return diffCustomizationSettings(currentSettings, newSettings);
    }, [currentSettings, newSettings]);

    const modified = diff.length > 0;

    // Set settings with a new value or function
    const setSettings = useEventCallback((update) => {
        setNewSettings((prev) => {
            const newSettings = update instanceof Function ? update(prev) : update;

            const diff = diffCustomizationSettings(prev, newSettings);

            analytics.logEvent(
                analytics.createSiteEvent(site, {
                    type: 'site_preview_publish_customization',
                    contentProperties: diff,
                    siteSpace: siteSpaceId,
                })
            );

            return newSettings;
        });
    });

    const setCustomizationSiteSpace = useEventCallback(
        (siteSpaceId: api.SiteSpace['id'] | undefined) => {
            navigate(
                {
                    screen: {
                        type: 'site',
                        site: site.id,
                        tab: {
                            type: 'customization',
                            siteSpaceId,
                            tab: selectedTab,
                        },
                    },
                },
                { replace: true }
            );
        }
    );

    const saveCustomizations = useEventCallback(async () => {
        await toast.catch(async () => {
            const siteSettingsToSave: api.SiteCustomizationSettings = {
                ...siteSettings,
                ...(!siteSpaceId ? newSettings : {}),
                // If user still needs to upgrade, we save but set advancedCustomization to false. This will be set to true once they upgraded.
                advancedCustomization: { enabled: !usesUnpaidCustomizations },
            };

            if (siteSpaceId) {
                // ignoring the 'title' override as the value for site spaces is set in site structures and we don't want the site title to override that.
                const { title, ...overriddenSettings } = computeSettingsOverrides(
                    currentSettings,
                    newSettings
                );

                await apiClient.siteCustomization.overrideSiteSpaceCustomizationById(
                    organizationId,
                    site.id,
                    siteSpaceId,
                    overriddenSettings
                );
            } else {
                await apiClient.siteCustomization.updateSiteCustomizationById(
                    organizationId,
                    site.id,
                    siteSettingsToSave
                );
            }

            analytics.logEvent(
                analytics.createSiteEvent(
                    { id: site.id, organization: organizationId },
                    {
                        type: 'site_edit_publish_customization',
                        contentProperties: diff,
                        siteSpace: siteSpaceId,
                    }
                )
            );

            const toastDetailsAddition = usesUnpaidCustomizations
                ? isSiteUpgrade
                    ? `${upgrade?.plan} features will show after upgrading.`
                    : 'New pricing features will show after upgrading.'
                : '';

            const toastMessage = site.published
                ? `Your site changes will be reflected soon. ${toastDetailsAddition}`
                : `Your site changes will be reflected after publishing. ${toastDetailsAddition}`;

            toast.success('Customization changed', {
                description: toastMessage,
            });
        }, 'Error while saving customization settings');
    });

    const siteSpaceIds = React.useMemo(
        () => siteSpaces.map((siteSpace) => siteSpace.space.id),
        [siteSpaces]
    );

    const customizationPanelProps: SitesCustomizationPanelProps = {
        settings: newSettings,
        setSettings,
        site,
        organizationId,
        siteSpaceId,
        upgrade: null,
        siteSpaceIds,
    };

    // If we have a specific siteSpaceId selected, we always preview that siteSpaceId. If we have the global theme settings selected in the picker, we should preview the default space.
    const previewSiteSpace = siteSpaceId
        ? siteSpaces.find((siteSpace) => siteSpace.id === siteSpaceId)
        : siteSpaces.find((siteSpace) => siteSpace.default);

    const requiresOnlyUpgrade = site.published && usesUnpaidCustomizations && !modified;
    const requiresSaveAndUpgrade = site.published && usesUnpaidCustomizations && modified;

    const getButtonState = (): ButtonProps => {
        if (requiresOnlyUpgrade) {
            return {
                testID: 'customization.saveButton',
                loadable: [loading, wrapLoading],
                kind: 'upgrade',
                label: 'Upgrade',
                leadingIcon: Icon.Upgrade,
                onPress: () => upgrade?.onPress(),
            };
        }

        if (requiresSaveAndUpgrade) {
            return {
                testID: 'customization.saveButton',
                loadable: [loading, wrapLoading],
                kind: 'upgrade',
                label: 'Save and upgrade',
                leadingIcon: Icon.Upgrade,
                onPress: async () => {
                    await saveCustomizations();
                    await upgrade?.onPress();
                },
            };
        }

        return {
            testID: 'customization.saveButton',
            loadable: [loading, wrapLoading],
            disabled: !modified,
            kind: 'primary',
            label: 'Save',
            onPress: () => saveCustomizations(),
        };
    };

    const headingId = React.useId();

    return (
        <>
            <BlockNavigationModal
                block={modified && !loading}
                acceptTransition={acceptScreenTransition}
            />
            <ContentReferenceContextProvider
                // Reset the entire context for content-ef to ensure it matches
                // how global customization settings (header/foooter links) will be applied
                value={{
                    organization: organizationId,
                    site: site.id,
                }}
            >
                <section
                    key={settingsKey}
                    className="flex flex-1 flex-col overflow-hidden lg:flex-row"
                    aria-labelledby={headingId}
                >
                    {/* Customization controls */}
                    <div
                        className={clsx(
                            'flex flex-1 flex-col overflow-auto',
                            'max-lg:max-w-full max-lg:flex-1',
                            'lg:max-w-lg lg:grow lg:border-r'
                        )}
                    >
                        <Heading
                            id={headingId}
                            level={1}
                            className="mt-4 px-6 text-ui-heading-large"
                        >
                            Site customization
                        </Heading>
                        <div className="sticky top-0 z-[1] flex flex-col gap-4 bg-base pt-2">
                            {shouldShowPicker ? (
                                <div className="flex px-6">
                                    <CustomizationSiteSpacePicker
                                        siteSpaceId={siteSpaceId}
                                        setCustomizationSiteSpace={setCustomizationSiteSpace}
                                        siteSpacesPickerItems={siteSpacesPickerItems}
                                        {...customizationPanelProps}
                                    />
                                </div>
                            ) : null}
                            <Tabs className="sticky top-0 z-[1] border-b bg-base px-6">
                                <Tab
                                    active={selectedTab === 'general'}
                                    onPress={() => {
                                        setSelectedTab('general', siteSpaceId);
                                    }}
                                    label="General"
                                />
                                <Tab
                                    active={selectedTab === 'layout'}
                                    onPress={() => {
                                        setSelectedTab('layout', siteSpaceId);
                                    }}
                                    label="Layout"
                                />
                                <Tab
                                    active={selectedTab === 'configure'}
                                    onPress={() => {
                                        setSelectedTab('configure', siteSpaceId);
                                    }}
                                    label="Configure"
                                />
                            </Tabs>
                        </div>
                        {/* Inject this context from a higher level to ensure that the panel is always styled correctly in side-panel. */}
                        <PanelSetContext.Provider value={{ variant: 'enclosed' }}>
                            <div className="flex flex-1 flex-col gap-6 bg-side p-6">
                                <div className="flex flex-col">
                                    {selectedTab === 'general' ? (
                                        <TabGeneral
                                            {...customizationPanelProps}
                                            siteSpaceId={siteSpaceId}
                                            siteSpaceName={
                                                siteSpacesPickerItems.find(
                                                    (item) => item.value === siteSpaceId
                                                )?.label
                                            }
                                        />
                                    ) : null}
                                    {selectedTab === 'layout' ? (
                                        <TabLayout {...customizationPanelProps} />
                                    ) : null}
                                    {selectedTab === 'configure' ? (
                                        <TabConfigure {...customizationPanelProps} />
                                    ) : null}
                                </div>
                            </div>
                        </PanelSetContext.Provider>
                        <Actions className="sticky bottom-0 z-[1] border-t bg-base px-6 py-4 lg:hidden">
                            <Button {...getButtonState()} />
                        </Actions>
                    </div>

                    {/* Customization preview */}
                    <div className="flex flex-col justify-between lg:flex-1">
                        <div className="flex size-full flex-col items-center justify-center pt-4 max-lg:hidden">
                            <SiteCustomizationPreview
                                organization={organization}
                                site={site}
                                previewSiteSpace={previewSiteSpace}
                                settings={newSettings}
                            />
                        </div>

                        <div className="flex shrink-0 justify-end gap-4 px-10 pt-4 pb-4 max-lg:hidden xl:pt-2">
                            <Button {...getButtonState()} />
                        </div>
                    </div>
                </section>
            </ContentReferenceContextProvider>
        </>
    );
}

/**
 * Return true if the user is staying on the current screen.
 */
function acceptScreenTransition(
    current: NavigationScreenState,
    next: NavigationScreenState
): boolean {
    return (
        current.type === 'site' &&
        next.type === 'site' &&
        current.site === next.site &&
        next.tab?.type === 'customization' &&
        current.tab?.type === 'customization' &&
        current.tab.siteSpaceId === next.tab.siteSpaceId
    );
}

/**
 * Check if any advanced customization settings are enabled.
 */
function checkHasAnyEnabledPaidCustomizationSetting(
    settings: api.SiteCustomizationSettings
): boolean {
    // @site-font - Keep it synced with backend by grepping that tag.
    const hasCustomFont =
        settings.styling.font &&
        (typeof settings.styling.font !== 'string' || !FREE_FONTS.includes(settings.styling.font));
    const hasCustomMonospaceFont =
        settings.styling.monospaceFont &&
        (typeof settings.styling.monospaceFont !== 'string' ||
            !FREE_FONTS.includes(settings.styling.monospaceFont));
    // @site-logo - Keep it synced with backend by grepping that tag.
    const hasLogo = Boolean(settings.header.logo);
    // @site-footer - Keep it synced with backend by grepping that tag.
    const hasFooter = Boolean(
        settings.footer?.copyright || settings.footer?.logo || settings.footer?.groups.length
    );
    // @site-announcement - Keep settings synced with backend by grepping that tag.
    const hasAnnouncement = Boolean(settings.announcement?.enabled);

    // @site-theme - Keep it synced with backend by grepping that tag.
    const hasPaidTheme = SITE_THEMES.some(
        (theme) => theme.value === settings.styling.theme && theme.needAdvancedCustomization
    );

    // @site-header-theme - Keep it synced with backend by grepping that tag.
    const hasCustomHeaderTheme = THEME_PRESETS.some(
        (preset) => preset.theme === settings.header.preset && preset.needAdvancedCustomization
    );
    // @site-icons - Keep it synced with backend by grepping that tag.
    const hasIconsStyle = settings.styling.icons !== api.CustomizationIconsStyle.Regular;

    // @site-semantic-colors - Keep it synced with backend by grepping that tag.
    const hasCustomSemanticColors = Object.entries({
        infoColor: DEFAULT_HINT_INFO_COLOR,
        successColor: DEFAULT_HINT_SUCCESS_COLOR,
        warningColor: DEFAULT_HINT_WARNING_COLOR,
        dangerColor: DEFAULT_HINT_DANGER_COLOR,
    } as const).some(
        ([key, defaultValue]) =>
            settings.styling[key].dark !== defaultValue ||
            settings.styling[key].light !== defaultValue
    );

    return (
        hasCustomFont ||
        hasCustomMonospaceFont ||
        hasLogo ||
        hasFooter ||
        hasAnnouncement ||
        hasCustomHeaderTheme ||
        hasIconsStyle ||
        hasPaidTheme ||
        hasCustomSemanticColors
    );
}
